import React, { useState } from 'react';
import { CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { format } from 'date-fns';

export default function ValidationStatusPanel({ reservations, onUpdateStatus, onRefresh }) {
  const [selectedId, setSelectedId] = useState(null);
  const [notes, setNotes] = useState('');
  const [showNotesModal, setShowNotesModal] = useState(false);

  const handleApprove = async (id) => {
    await onUpdateStatus(id, 'confirmed', 'Approuvée par admin');
    onRefresh();
  };

  const handleReject = (id) => {
    setSelectedId(id);
    setShowNotesModal(true);
  };

  const handleRejectConfirm = async () => {
    if (selectedId && notes) {
      await onUpdateStatus(selectedId, 'cancelled', notes);
      setShowNotesModal(false);
      setSelectedId(null);
      setNotes('');
      onRefresh();
    }
  };

  const getDataCompleteness = (reservation) => {
    const fields = [
      reservation?.customer_name,
      reservation?.customer_phone,
      reservation?.pickup_address,
      reservation?.delivery_address,
      reservation?.collection_date
    ];
    const filledFields = fields?.filter(f => f)?.length;
    const percentage = (filledFields / fields?.length) * 100;
    return { percentage, filledFields, totalFields: fields?.length };
  };

  const getCompletenessColor = (percentage) => {
    if (percentage === 100) return 'text-green-600 bg-green-100';
    if (percentage >= 80) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Réservations en Attente de Validation</h3>
        <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-semibold">
          {reservations?.length} en attente
        </span>
      </div>

      {/* Reservations Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {reservations?.length === 0 ? (
          <div className="col-span-full bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 p-12 text-center">
            <CheckCircle className="mx-auto text-gray-400 mb-4" size={48} />
            <p className="text-gray-600">Aucune réservation en attente de validation</p>
          </div>
        ) : (
          reservations?.map((reservation) => {
            const completeness = getDataCompleteness(reservation);
            return (
              <div key={reservation?.id} className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-lg transition-shadow">
                {/* Header */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900">{reservation?.customer_name}</h4>
                    <p className="text-sm text-gray-600">{reservation?.customer_phone}</p>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getCompletenessColor(completeness?.percentage)}`}>
                    {completeness?.percentage?.toFixed(0)}%
                  </span>
                </div>

                {/* Details */}
                <div className="space-y-2 mb-4">
                  <div className="flex items-start space-x-2 text-sm">
                    <span className="text-gray-500 font-medium min-w-[80px]">Collecte:</span>
                    <span className="text-gray-900">{reservation?.pickup_address}</span>
                  </div>
                  <div className="flex items-start space-x-2 text-sm">
                    <span className="text-gray-500 font-medium min-w-[80px]">Livraison:</span>
                    <span className="text-gray-900">{reservation?.delivery_address}</span>
                  </div>
                  {reservation?.collection_date && (
                    <div className="flex items-start space-x-2 text-sm">
                      <span className="text-gray-500 font-medium min-w-[80px]">Date:</span>
                      <span className="text-gray-900">{format(new Date(reservation?.collection_date), 'dd/MM/yyyy')}</span>
                    </div>
                  )}
                  {reservation?.service_type && (
                    <div className="flex items-start space-x-2 text-sm">
                      <span className="text-gray-500 font-medium min-w-[80px]">Service:</span>
                      <span className="text-gray-900">{reservation?.service_type}</span>
                    </div>
                  )}
                </div>

                {/* Completeness Bar */}
                <div className="mb-4">
                  <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                    <span>Complétude des données</span>
                    <span>{completeness?.filledFields}/{completeness?.totalFields} champs</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all ${
                        completeness?.percentage === 100 ? 'bg-green-600' :
                        completeness?.percentage >= 80 ? 'bg-yellow-600' : 'bg-red-600'
                      }`}
                      style={{ width: `${completeness?.percentage}%` }}
                    ></div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleApprove(reservation?.id)}
                    className="flex-1 flex items-center justify-center space-x-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <CheckCircle size={16} />
                    <span className="text-sm font-medium">Approuver</span>
                  </button>
                  <button
                    onClick={() => handleReject(reservation?.id)}
                    className="flex-1 flex items-center justify-center space-x-2 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    <XCircle size={16} />
                    <span className="text-sm font-medium">Rejeter</span>
                  </button>
                </div>

                {/* Timestamp */}
                <div className="mt-3 pt-3 border-t border-gray-200 text-xs text-gray-500 text-center">
                  Reçu le {format(new Date(reservation?.created_at), 'dd/MM/yyyy à HH:mm')}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Reject Modal */}
      {showNotesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertCircle className="text-red-600" size={24} />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Rejeter la Réservation</h3>
            </div>

            <p className="text-gray-600 mb-4">Veuillez indiquer la raison du rejet :</p>

            <textarea
              value={notes}
              onChange={(e) => setNotes(e?.target?.value)}
              rows={4}
              placeholder="Raison du rejet..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent mb-4"
            />

            <div className="flex items-center space-x-3">
              <button
                onClick={handleRejectConfirm}
                disabled={!notes}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Confirmer le Rejet
              </button>
              <button
                onClick={() => {
                  setShowNotesModal(false);
                  setSelectedId(null);
                  setNotes('');
                }}
                className="flex-1 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}